import { KeyboardEvent, RefObject, useEffect, useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  MiddlewareArguments,
  size,
  useClick,
  useDismiss,
  useFloating,
  UseFloatingProps,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react-dom-interactions';
import { ChakraProps } from '@chakra-ui/react';
import { mergeRefs } from '@chakra-ui/react-utils';
import useAutoScroll from './useAutoScroll';

type ApplyFnArgs = MiddlewareArguments & {
  availableWidth: number;
  availableHeight: number;
};
const useFloatingDropdown = ({
  dropdownWidth,
  enabled,
  optionsRef,
  placement,
}: {
  enabled: boolean;
  optionsRef: RefObject<HTMLDivElement>;
  dropdownWidth: ChakraProps['width'] | 'match';
  placement: UseFloatingProps['placement'] | undefined;
}) => {
  const listRef = useRef<HTMLElement[]>([]);
  const [isOpen, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [keyboardControlled, setKeyboardControlled] = useState(true);
  useEffect(() => {
    setKeyboardControlled(true);
  }, [isOpen]);

  const widthRef = useRef(dropdownWidth);
  widthRef.current = dropdownWidth;
  const { context, floating, reference, strategy, x, y } = useFloating<Element>({
    middleware: [
      size({
        apply: ({ availableHeight, elements, rects }: ApplyFnArgs) => {
          Object.assign(elements.floating.style, {
            width: widthRef.current === 'match' ? `${rects.reference.width}px` : widthRef.current,
          });
          elements.floating.style.setProperty('--floating-available-height', `${availableHeight}px`);
        },
        padding: 5,
      }),
      flip(),
    ],
    onOpenChange: setOpen,
    open: enabled && isOpen,
    placement,
    whileElementsMounted(aRef, aFloat, update) {
      return autoUpdate(aRef, aFloat, update, { elementResize: false });
    },
  });

  const keyboardControlledHandlers = {
    onKeyDown(ev: KeyboardEvent) {
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        setOpen(false);
      }
      setKeyboardControlled(true);
    },
    onPointerEnter() {
      setKeyboardControlled(false);
    },
    onPointerMove() {
      setKeyboardControlled(false);
    },
  };
  const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions([
    useClick(context, { enabled }),
    useRole(context, { role: 'listbox' }),
    useDismiss(context),
    useListNavigation(context, {
      activeIndex,
      enabled,
      listRef,
      loop: true,
      onNavigate: setActiveIndex,
      selectedIndex,
      virtual: true,
    }),
    {
      floating: keyboardControlledHandlers,
      reference: { onKeyDown: keyboardControlledHandlers.onKeyDown },
    },
  ]);
  useAutoScroll({
    activeIndex,
    keyboardControlled,
    listRef,
    optionsRef,
    selectedIndex,
  });

  return {
    activeIndex,
    close: () => setOpen(false),
    context,
    floatingProps: getFloatingProps({
      ref: floating,
      style: { left: x ?? 0, position: strategy, top: y ?? 0 },
    }),
    getItemProps,
    getReferenceProps({ ref, ...props }: React.HTMLProps<Element>) {
      const merged = mergeRefs(ref as any, reference);
      return getReferenceProps({ ...props, ref: merged });
    },
    isOpen,
    listRef,
    setActiveIndex,
    setSelectedIndex,
  };
};

export default useFloatingDropdown;
